import { useCallback } from 'react';
import { toast } from 'sonner';

import type { WebSocketMessage } from '@famecreator/shared-types/websocket';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { useInvalidateUserData } from '@/shared/hooks/use-user-data';
import { useWebSocketMessages } from '@/shared/hooks/use-websocket-messages';
import { logger } from '@/shared/lib/logger';

/**
 * Event kinds that move credits between holds and the raw balance without
 * changing what the customer can spend. We never surface a toast for these
 * — the playground UI already animates the change inline. Toasts are reserved
 * for "fresh credits arrived" (purchase / topup / refund) so the customer is
 * never surprised by a balance bump while they're working.
 */
// inference_charge has amountAdded < 0 so it wouldn't trigger the positive-toast
// branch anyway, but include it here defensively so a future refactor of the
// guard doesn't accidentally start toasting on every successful generation.
const HOLD_LIFECYCLE_TYPES = new Set(['inference_hold', 'inference_release', 'inference_deduct', 'inference_charge']);

export const useCreditUpdates = () => {
  const setCreditBalance = useAuthStore((s) => s.setCreditBalance);
  const setCreditsLoading = useAuthStore((s) => s.setCreditsLoading);
  const { invalidateUserData } = useInvalidateUserData();

  const handleCreditMessage = useCallback(
    (message: WebSocketMessage) => {
      if (!message.creditUpdate) return;

      const { amountAdded, totalCredits, type } = message.creditUpdate;
      // Backward-compat with legacy emitters (inline-deduct path during the
      // transition) that don't carry the hold-aware trio: fall back to 0 for
      // heldCredits and to totalCredits (= raw balance) for availableCredits.
      const heldCredits = message.creditUpdate.heldCredits ?? 0;
      const availableCredits = message.creditUpdate.availableCredits ?? totalCredits;

      // Push the full trio into the auth store in one set() so consumers
      // (HeaderCredits, BillingPage, etc.) see a consistent snapshot.
      setCreditBalance({
        credits: totalCredits,
        heldCredits,
        availableCredits,
      });
      setCreditsLoading(false);

      // Invalidate cached user data so it gets refreshed with new credits
      // This is more efficient than calling /auth/me immediately.
      invalidateUserData();

      // Toast only when the event genuinely added spendable credits — i.e.
      // purchases / top-ups / refunds. Hold-lifecycle events (inference_hold,
      // inference_release, inference_deduct) reshuffle credits between holds
      // and the raw balance and should NOT toast.
      if (amountAdded > 0 && !HOLD_LIFECYCLE_TYPES.has(type)) {
        const toastMessage =
          type === 'purchase_credits'
            ? `💳 Credits purchased: +${amountAdded}`
            : `✨ Credits added: +${amountAdded}`;

        toast.success(toastMessage, {
          duration: 4000,
          position: 'top-center',
        });
      }

      logger.auth.debug('Credit update received - cache invalidated', {
        amountAdded,
        totalCredits,
        heldCredits,
        availableCredits,
        type,
        timestamp: message.creditUpdate.timestamp,
      });
    },
    [setCreditBalance, setCreditsLoading, invalidateUserData],
  );

  useWebSocketMessages({
    enabled: true,
    onMessage: handleCreditMessage,
  });
};
